import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import Landing from '@lt/components/icons-react/userflow/Landing';
import Flight from '@lt/components/icons-react/Flight';

import ApplyButton from '../ApplyButton';
import NightsInfo from '../NightsInfo';
import { rightArrowSymbol, interpunctSymbol } from '../../constants';
import FlexDatesPickerComponent from '../Calendar/components/FlexDatesPicker';

import { convertDatePickerParamsToStrings } from '../../../../utils';

import type { FooterProps } from './types';

export const Footer = (props: FooterProps): JSX.Element | null => {
  const {
    isMobile = false,
    applyButtonHandler,
    leftValue,
    rightValue,
    startDatePreset,
    nights,
    nightsPreset,
    isHotelOnly,
    selectPresetTab,
    isVacationOptionsAvailable,
    onChangeShift,
    currentShift,
    isFlexDatesOff,
  } = props;

  const { left, right, isPreset } = convertDatePickerParamsToStrings({
    leftValue,
    rightValue,
    startDatePreset,
    nights,
    nightsPreset,
  });

  const delimiterSymbol = isPreset ? interpunctSymbol : rightArrowSymbol;

  const isApplyButton = left && right;
  const isLandingIcon = !isHotelOnly && !isPreset;
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (buttonRef.current)
      buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  return (
    <StyledFooter $isMobile={isMobile}>
      {!isFlexDatesOff && (
        <FlexDatesPicker onChangeShift={onChangeShift} currentShift={currentShift} />
      )}
      {isApplyButton ? (
        <ApplyButton onClick={applyButtonHandler} buttonRef={buttonRef}>
          <DatesText>
            {!isHotelOnly ? <PlaneIcon /> : null}
            {left}
            <DelimiterContainer>{delimiterSymbol}</DelimiterContainer>
            {isLandingIcon ? <LandingIcon /> : null}
            {right}
          </DatesText>
          <NightsInfo
            startDateCell={leftValue}
            endDateCell={rightValue}
            nights={nights || nightsPreset?.value}
            isMobile={isMobile}
            selectPresetTab={selectPresetTab}
            isVacationOptionsAvailable={isVacationOptionsAvailable}
          />
        </ApplyButton>
      ) : null}
    </StyledFooter>
  );
};

const FlexDatesPicker = styled(FlexDatesPickerComponent)`
  margin: 0 0 12px 0;
`;

const StyledFooter = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  clear: both;

  ${({ $isMobile }) =>
    $isMobile &&
    `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 8px;
      background: #fff;
      width: 100%;
      z-index: 1;
    `};
`;

const DatesText = styled.div`
  display: flex;
  align-items: center;
`;

const LandingIcon = styled(Landing)`
  stroke: #fff;
  width: 20px;
  height: 20px;
  align-self: end;
`;

const PlaneIcon = styled(Flight)`
  stroke: #fff;
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const DelimiterContainer = styled.span`
  font-family: cursive;
  padding: 0 4px;
`;
